<template>
  <div
    v-if="currentStepData"
    :style="`--stepColor: #${currentStepData.color}`"
    class="category-wizard-mobile"
  >
    <div class="safe-horizontal-container">
      <div class="category-wizard-mobile__header">
        <div
          class="category-wizard-mobile__meta"
          role="status"
          aria-live="polite"
          :aria-label="a11yStatusLabel"
        >
          <span class="category-wizard-mobile__meta-left">
            {{ stepCountLabel }}
          </span>
          <span class="category-wizard-mobile__meta-right">
            {{ completionLabel }}
          </span>
        </div>

        <CategoryColorBar
          :active-step="currentStep"
          class="category-wizard-mobile__bar"
        />

        <div class="category-wizard-mobile__title-row">
          <span class="category-wizard-mobile__category-name">
            {{ currentStepData.label }}
          </span>
          <ElTag
            v-if="showLevelTag"
            :size="$screen.smDown ? 'small' : 'default'"
            class="category-wizard-mobile__category-level"
            data-tag-type="grey"
            effect="plain"
          >
            {{ $t("common.levelTag", { level: selectedStepData?.level }) }}
          </ElTag>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";

import CategoryColorBar from "@/modules/affiliates/components/category-color-bar/category-color-bar.vue";

import { ICategoryLevelSelection } from "@/services/data/viral-level/viral-level.interface";
import { ICategory } from "@/services/data/category/category.interface";
import { ICategoryStepItem } from "@/modules/affiliates/views/affiliate.types";
import { ILevel } from "@/services/data/level/level.interface";

export default defineComponent({
  name: "CategoryWizardMobile",

  components: {
    CategoryColorBar,
  },

  props: {
    steps: {
      type: Array as () => ICategoryStepItem[],
      required: true,
    },

    currentStep: {
      type: Number,
      default: 0,
    },

    active: {
      type: Number,
      default: null,
    },
  },

  computed: {
    totalSteps(): number {
      return this.steps?.length || 0;
    },

    currentStepNumber(): number {
      if (this.totalSteps <= 0) return 0;
      return Math.min(this.currentStep + 1, this.totalSteps);
    },

    assessmentLevels(): Array<ICategoryLevelSelection> {
      return (
        this.$store.state.affiliates.draftSubmission?.data.assessment || []
      );
    },

    /**
     * Get all categories from the store.
     */
    categories(): Array<ICategory> {
      return this.$store.state.categories.data;
    },

    /**
     * Information of the current selected step.
     */
    currentStepData(): ICategoryStepItem {
      return this.steps[this.currentStep];
    },

    /**
     * Get the category for the current step.
     */
    currentCategory(): ICategory | null {
      if (this.categories.length === 0) {
        return null;
      }

      return this.categories[this.currentStep];
    },

    /**
     * Get the data for the selected step.
     */
    selectedStepData(): ICategoryLevelSelection | null {
      if (!this.assessmentLevels || this.assessmentLevels.length === 0) {
        return null;
      }

      const selectedDetail = this.assessmentLevels.find(
        (record: ICategoryLevelSelection): any => {
          return (
            this.currentCategory && record.category === this.currentCategory.id
          );
        },
      );

      return selectedDetail || null;
    },

    /**
     * Informs if the level tag must be shown.
     *
     * The tag only is visible when there is a level selected.
     */
    showLevelTag(): boolean {
      return (
        !!this.selectedStepData &&
        (this.selectedStepData.level as ILevel["value"]) > 0
      );
    },

    completedStepsCount(): number {
      const stepIds = new Set(this.steps.map((step) => step.value));
      return this.assessmentLevels.filter(
        (record: ICategoryLevelSelection) =>
          stepIds.has(record.category) &&
          ((record.level as ILevel["value"]) || 0) > 0,
      ).length;
    },

    completionPercent(): number {
      if (this.totalSteps <= 0) return 0;
      const denominator = Math.max(this.totalSteps - 1, 1);
      const numerator = Math.min(this.completedStepsCount, denominator);
      return Math.round((numerator / denominator) * 100);
    },

    stepCountLabel(): string {
      return `Step ${this.currentStepNumber} of ${this.totalSteps}`;
    },

    completionLabel(): string {
      return `${this.completionPercent}% Complete`;
    },

    a11yStatusLabel(): string {
      return `${this.stepCountLabel}. ${this.completionLabel}.`;
    },
  },
});
</script>

<style lang="scss" scoped>
.category-wizard-mobile {
  position: relative;
  display: block;
  z-index: 2;
  width: 100%;

  background: white;

  &::after {
    position: relative;
    display: block;
    width: 100%;
    height: 4px;

    content: "";

    background-color: var(--stepColor);
  }

  &__header {
    padding: 10px 0 12px;
  }

  &__meta {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    margin-bottom: 6px;
    padding: 0 15px;
    color: $manatee;
    letter-spacing: 0.2px;
  }

  &__meta-left,
  &__meta-right {
    @include grotesk(medium);
    font-size: 1rem;
    line-height: 1.2;
  }

  &__meta-right {
    text-align: right;
  }

  &__title-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 10px 15px 0;
    min-width: 0;
  }

  &__bar {
    padding: 2px 15px 0;
  }

  &__bar :deep(.category-color-bar__color) {
    height: 14px;
  }

  &__category-name {
    @include grotesk(semiBold);

    font-size: 2.125rem;
    line-height: 1.05;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__category-level {
    flex-shrink: 0;
    background-color: var(--stepColor);
    border: solid 1px rgba(0, 0, 0, 0.06);
  }
}
</style>
